/**
 * User-facing help text overlay (Phase 7d-2).
 *
 * The catalog's `field.helpText` is engineer-facing — it cites USCIS
 * widget IDs ("I-485 Pt8Line63_CB[0..4]"), legacy property names
 * ("Legacy applicant UI stored this as aNumber"), and notes for the
 * pdf-engine maintainers. Those notes belong in the catalog as the
 * source of truth for auditors, but they shouldn't render to a real
 * applicant filling out the form.
 *
 * This overlay maps `FieldId → user-facing help string`. The
 * FieldRenderer resolves the overlay first; if no entry exists, it
 * falls back to `field.helpText` (which is what every form rendered
 * before this overlay landed).
 *
 * Authoring guide:
 * - Second person ("Your", "you").
 * - One or two sentences max — the field label carries the question;
 *   help text adds context, not restatement.
 * - Cite USCIS conventions sparingly; never paste widget IDs or
 *   internal codes.
 * - Anxiety-aware tone — applicants are filing high-stakes paperwork.
 *   "Best guess is fine" beats "exact answer required" where USCIS
 *   instructions allow estimation.
 *
 * The empty string `''` is a deliberate value — it means "the catalog's
 * engineer-facing helpText should be cleared but no user-facing copy is
 * needed; the field label is self-explanatory." The primitive renderers
 * skip both `undefined` and `''`, so an empty-string override hides the
 * engineer-speak without a redundant restatement.
 */

import type { FieldDefinition, FieldId } from '@inside-us-aos/domain';

export const USER_HELP_TEXT: Readonly<Record<string, string>> = {
  // === Identity / Personal info (Phase 7d-2 chunk 1) ===
  // Section: 'identity' — name, DOB, place of birth, sex,
  // citizenship, A-number, USCIS account, contact details.

  'beneficiary.name.legal.first':
    'As it appears on your passport or birth certificate.',
  'beneficiary.name.legal.middle': "Leave blank if you don't have one.",
  'beneficiary.name.legal.last':
    'Family name as it appears on your passport or birth certificate.',

  'beneficiary.name.other.has_used':
    "Maiden names, names from a prior marriage, names on prior immigration paperwork. Skip casual nicknames.",
  'beneficiary.name.other.first': '',
  'beneficiary.name.other.middle': '',
  'beneficiary.name.other.last': '',

  'beneficiary.birth.date': 'As it appears on your birth certificate.',
  'beneficiary.birth.additional.has_used':
    "Only if an official record shows a different date — not for casual typos.",
  'beneficiary.birth.additional.count': '',
  'beneficiary.birth.additional.date': '',

  // Place-of-birth helpers — kept terse. The composite legend
  // ("Place of birth") + the field labels (City / Region / Country)
  // already say what each field captures; helper text should only
  // add what isn't obvious. Country gets a one-liner about modern
  // naming (matters for USSR/Yugoslavia generation); region gets
  // the "skip if N/A" hint; city has nothing else worth saying.
  'beneficiary.birth.place.city': '',
  'beneficiary.birth.place.region': 'Skip if not applicable.',
  'beneficiary.birth.place.country':
    'Use the current country name (e.g., Russia, not USSR).',

  'beneficiary.demographics.sex':
    "Use the sex on your most recent USCIS document, or your birth certificate.",

  'beneficiary.citizenship.primary_country':
    "Country whose passport you use, or that considers you a citizen by birth.",
  'beneficiary.citizenship.has_secondary_country': '',
  'beneficiary.citizenship.secondary_country': '',

  'beneficiary.identifiers.alien_registration_number':
    "7-9 digit USCIS number on your green card, EAD, or USCIS notices. Leave blank if you've never had one.",
  'beneficiary.identifiers.other_alien_registration_number':
    "Most applicants don't have one — leave blank if so.",
  'beneficiary.identifiers.uscis_online_account_number':
    "Top-right of your myUSCIS dashboard. Leave blank if you don't have an account.",

  'beneficiary.contact.daytime_phone':
    'A number USCIS can reach you at during business hours.',
  'beneficiary.contact.mobile_phone':
    'If different from the daytime phone.',
  'beneficiary.contact.email':
    'USCIS may send case-status updates here.',

  // === Where you live (Phase 7d-2 chunk 2) ===
  // Section: 'physical_address' — current address, mailing address
  // (with same-as toggle), 5-year address history (repeatable),
  // safe-address request boolean (VAWA / SIJ / T / U).
  //
  // Most address sub-fields use the empty-string convention to clear
  // catalog engineer-speak without restating the label. Copy is
  // authored only on toggles and fields with non-obvious semantics.

  // Toggles & gates.
  'beneficiary.mailing_address.same_as_current_physical':
    'Pick No only if you get mail somewhere else (P.O. box, in-care-of relative).',
  'beneficiary.safe_mailing_address.requested':
    "VAWA / T / U status applicants can request mail at a different address. Most applicants pick No.",

  // Current physical address —
  //
  // Most helper text dropped to ''.
  // The composite labels (Country, Street address, City, State,
  // ZIP code / PIN code / Postal code) now carry the meaning;
  // helper text was restating the label. Only the country field
  // keeps a brief helper that explains why it's first.
  'beneficiary.current_physical_address.street_number_and_name': '',
  'beneficiary.current_physical_address.unit_type': '',
  'beneficiary.current_physical_address.unit_number': '',
  'beneficiary.current_physical_address.city': '',
  'beneficiary.current_physical_address.state': '',
  'beneficiary.current_physical_address.zip_code': '',
  'beneficiary.current_physical_address.country': '',
  'beneficiary.current_physical_address.province': '',
  'beneficiary.current_physical_address.postal_code': '',
  'beneficiary.current_physical_address.residence.date_from':
    "Approximate month is fine.",

  // Mailing address — same Slice 27 cleanup as current physical.
  // in_care_of_name keeps its helper because the C/O concept isn't
  // universally familiar; everything else relies on the composite
  // label.
  'beneficiary.mailing_address.in_care_of_name':
    "Full name on the mailbox (e.g., 'C/O Jane Smith').",
  'beneficiary.mailing_address.street_number_and_name': '',
  'beneficiary.mailing_address.unit_type': '',
  'beneficiary.mailing_address.unit_number': '',
  'beneficiary.mailing_address.city': '',
  'beneficiary.mailing_address.state': '',
  'beneficiary.mailing_address.zip_code': '',
  'beneficiary.mailing_address.country': '',
  'beneficiary.mailing_address.province': '',
  'beneficiary.mailing_address.postal_code': '',

  // Address history (5-year repeatable group). USCIS asks for every
  // address you've lived at over the past 5 years; gaps are OK if
  // documented.
  'beneficiary.address_history.street_number_and_name': '',
  'beneficiary.address_history.unit_type': '',
  'beneficiary.address_history.unit_number': '',
  'beneficiary.address_history.city': '',
  'beneficiary.address_history.state': '',
  'beneficiary.address_history.zip_code': '',
  'beneficiary.address_history.country': '',
  'beneficiary.address_history.province': '',
  'beneficiary.address_history.postal_code': '',
  'beneficiary.address_history.date_from': 'Approximate month is fine.',
  'beneficiary.address_history.date_to':
    "Leave blank if you still live here.",

  // === Family — marriage (Phase 7d-2 chunk 3) ===
  // Section: 'marriage' — current marital status, current spouse info,
  // marriage place + date, prior marriages (repeatable), date and
  // place each prior marriage ended.

  'beneficiary.marital.status':
    "If you're filing as a spouse of a U.S. citizen or LPR, this should be Married.",
  'beneficiary.marital.times_married':
    'Include the current marriage. Annulments declared void from the start do not count.',
  'beneficiary.marital.current_spouse_is_military':
    "Pick Not applicable if you're not currently married.",

  // Current spouse — name + bio.
  'beneficiary.current_spouse.name.legal.first': '',
  'beneficiary.current_spouse.name.legal.middle':
    "Leave blank if none.",
  'beneficiary.current_spouse.name.legal.last': '',
  'beneficiary.current_spouse.alien_registration_number':
    "Leave blank for U.S. citizens by birth or those never issued one.",
  'beneficiary.current_spouse.birth.date': '',
  // Spouse place-of-birth — same terse treatment as beneficiary's
  // own birth fields above. Composite legend names the bag.
  'beneficiary.current_spouse.birth.place.city': '',
  'beneficiary.current_spouse.birth.place.region': 'Skip if not applicable.',
  'beneficiary.current_spouse.birth.place.country': '',

  // Marriage details.
  'beneficiary.current_spouse.marriage.date':
    'When the marriage was legally registered.',
  'beneficiary.current_spouse.marriage.place.city': '',
  'beneficiary.current_spouse.marriage.place.region': '',
  'beneficiary.current_spouse.marriage.place.country': '',
  'beneficiary.current_spouse.applying_concurrently':
    'Only if they have their own derivative application filed with yours.',

  // Prior marriages (repeatable group).
  'beneficiary.prior_marriages.spouse.name.legal.first': '',
  'beneficiary.prior_marriages.spouse.name.legal.middle': '',
  'beneficiary.prior_marriages.spouse.name.legal.last': '',
  'beneficiary.prior_marriages.spouse.birth.date':
    "If you know it.",
  'beneficiary.prior_marriages.date_started':
    'When the marriage was legally registered.',
  'beneficiary.prior_marriages.place_started.city': '',
  'beneficiary.prior_marriages.place_started.region': '',
  'beneficiary.prior_marriages.place_started.country': '',
  'beneficiary.prior_marriages.date_ended':
    'Divorce decree, death, or annulment date.',
  'beneficiary.prior_marriages.place_ended.city': '',
  'beneficiary.prior_marriages.place_ended.region': '',
  'beneficiary.prior_marriages.place_ended.country': '',

  // === Family — children & parents (Phase 7d-2 chunk 4) ===
  // Sections: 'children' (count + repeatable group), 'parents'
  // (parent_one + parent_two singletons).

  // Children.
  'beneficiary.children.count':
    "Every living child — biological, adopted, stepchildren — even if they don't live with you.",
  'beneficiary.children.name.legal.first': '',
  'beneficiary.children.name.legal.middle': "Leave blank if none.",
  'beneficiary.children.name.legal.last': '',
  'beneficiary.children.alien_registration_number':
    "Leave blank if they don't have one (most don't).",
  'beneficiary.children.birth.date': '',
  'beneficiary.children.birth.country': '',
  'beneficiary.children.applying_concurrently':
    'Only if they have their own derivative application filed with yours.',

  // Parents — USCIS asks for both even if deceased or out of contact.
  'beneficiary.parent_one.name.legal.first': '',
  'beneficiary.parent_one.name.legal.middle': '',
  'beneficiary.parent_one.name.legal.last': '',
  'beneficiary.parent_one.name.at_birth.first':
    "Original name if changed since birth. Leave blank if unchanged.",
  'beneficiary.parent_one.name.at_birth.middle': '',
  'beneficiary.parent_one.name.at_birth.last': '',
  'beneficiary.parent_one.birth.date':
    "Best estimate to the year is fine.",
  'beneficiary.parent_one.demographics.sex': '',
  'beneficiary.parent_one.birth.place.city': '',
  'beneficiary.parent_one.birth.place.country': '',
  'beneficiary.parent_one.current_residence.city':
    "If deceased, where they lived at time of death.",
  'beneficiary.parent_one.current_residence.country': '',

  // Parent 2 mirrors Parent 1.
  'beneficiary.parent_two.name.legal.first': '',
  'beneficiary.parent_two.name.legal.middle': '',
  'beneficiary.parent_two.name.legal.last': '',
  'beneficiary.parent_two.name.at_birth.first':
    "Original name if changed since birth.",
  'beneficiary.parent_two.name.at_birth.middle': '',
  'beneficiary.parent_two.name.at_birth.last': '',
  'beneficiary.parent_two.birth.date': '',
  'beneficiary.parent_two.demographics.sex': '',
  'beneficiary.parent_two.birth.place.city': '',
  'beneficiary.parent_two.birth.place.country': '',
  'beneficiary.parent_two.current_residence.city':
    "If deceased, where they lived at time of death.",
  'beneficiary.parent_two.current_residence.country': '',

  // === Background — immigration history, travel history, travel doc (Phase 7d-2 chunk 5) ===
  // Sections: 'immigration_history', 'travel_history', 'travel_document'.

  // Social Security.
  'beneficiary.ssn.consent_disclosure':
    'Pick No if you have never had a Social Security card.',
  'beneficiary.ssn.number':
    "9-digit U.S. SSN. Leave blank if never issued one.",

  // Travel document used at last arrival.
  'beneficiary.travel_document.expiration_date':
    'From the passport/travel doc used on your last entry.',
  'beneficiary.travel_document.issuing_country': '',
  'beneficiary.visa.number':
    "Visa number printed in red on the visa sticker. Leave blank for visa-waiver, parole, or no inspection.",

  // Last arrival into the U.S.
  'beneficiary.last_arrival.place.city':
    'Where you went through immigration (usually the airport city, e.g., Newark, LAX, Miami).',
  'beneficiary.last_arrival.place.state': '',
  'beneficiary.last_arrival.date':
    'Check your I-94 stamp or i94.cbp.dhs.gov.',
  'beneficiary.last_arrival.admitted_as':
    'Visa class on your I-94 (e.g., F-1, B-2, H-1B). Leave blank if paroled.',
  'beneficiary.last_arrival.paroled_as':
    'Parole category (humanitarian, Cuban, advance parole). Leave blank if admitted on a visa.',
  'beneficiary.last_arrival.other_explanation':
    "If you entered without inspection or other unusual way, briefly explain.",

  // Current immigration status.
  'beneficiary.immigration_status.current':
    'Leave blank if your status is the same as on your I-94.',

  // Prior immigrant visa.
  'beneficiary.prior_immigrant_visa.location.city':
    'Embassy/consulate city. Leave blank if this is your first immigrant-visa application.',
  'beneficiary.prior_immigrant_visa.location.country': '',
  'beneficiary.prior_immigrant_visa.decision':
    'Approved, refused, denied, or withdrawn.',
  'beneficiary.prior_immigrant_visa.decision_date': '',

  // Immigration proceedings (deportation, removal, etc.).
  'beneficiary.proceedings.type':
    "Removal / exclusion / rescission / judicial. Your attorney or court records will know.",
  'beneficiary.proceedings.date': '',
  'beneficiary.proceedings.place.city': '',
  'beneficiary.proceedings.place.state': '',

  // Travel document — Form I-131 fields.
  'application.travel_document.requested_type':
    'For marriage-based AOS, this is usually "Advance parole (I-485 pending)" — lets you travel while waiting without abandoning the case.',
  'application.travel_document.requested_type_receipt_number':
    "Leave blank if you're mailing I-131 with your I-485. Fill in only if I-485 was filed separately and you have the receipt notice.",
  'application.travel_document.previous_document_issue_date':
    'Most recent prior US travel doc. Leave blank if first request.',
  'application.travel_document.previous_document_disposition':
    'Issued / lost / stolen / never issued.',
  'application.travel_document.trip.proposed_departure_date': '',
  'application.travel_document.trip.expected_length_days': '',
  'application.travel_document.trip.purpose_description':
    'Plain English, e.g., "Visit family in Mexico City for a wedding".',
  'application.travel_document.trip.countries_to_visit':
    "Comma-separated is fine.",
  'application.travel_document.trip.intended_use':
    'One trip vs. multiple before the document expires.',

  // === Background — employment (Phase 7d-2 chunk 6) ===
  // Sections: 'employment' (applicant repeatable), plus sponsor
  // employment & current_employment (in 'financials' section but
  // closely related, authored together for consistency).

  'beneficiary.employment_history.employer_name':
    "Use 'Self-employed' for self-employment, 'Unemployed' for gaps — USCIS wants every month accounted for.",
  'beneficiary.employment_history.occupation':
    "Job title (e.g., 'Software engineer', 'Stay-at-home parent', 'Student', 'Unemployed').",
  'beneficiary.employment_history.address.street_number_and_name':
    "For remote work, the employer's main office.",
  'beneficiary.employment_history.address.unit_number': '',
  'beneficiary.employment_history.address.city': '',
  'beneficiary.employment_history.address.state': '',
  'beneficiary.employment_history.address.zip_code': '',
  'beneficiary.employment_history.address.province': '',
  'beneficiary.employment_history.address.postal_code': '',
  'beneficiary.employment_history.date_from': '',
  'beneficiary.employment_history.date_to':
    "Leave blank if you still work here.",

  // Sponsor employment (lives in financials section but identical shape).
  'sponsor.employment_history.employer_name':
    "Use 'Self-employed' or business name for self-employment.",
  'sponsor.employment_history.occupation': '',
  'sponsor.employment_history.address.street_number_and_name': '',
  'sponsor.employment_history.address.unit_number': '',
  'sponsor.employment_history.address.city': '',
  'sponsor.employment_history.address.state': '',
  'sponsor.employment_history.address.zip_code': '',
  'sponsor.employment_history.address.province': '',
  'sponsor.employment_history.address.postal_code': '',
  'sponsor.employment_history.date_from': '',
  'sponsor.employment_history.date_to': '',

  // Sponsor current_employment.
  'sponsor.current_employment.employed_as': '',
  'sponsor.current_employment.employer_one_name': '',
  'sponsor.current_employment.employer_two_name':
    'Only if you work two jobs.',
  'sponsor.current_employment.self_employed_as':
    'E.g., "Freelance graphic designer", "Restaurant owner".',
  'sponsor.current_employment.retired_since_date': '',
  'sponsor.current_employment.unemployed_since_date':
    'When you last worked.',

  // === Eligibility (Phase 7d-2 chunk 6 — continued) ===
  // Sections: 'eligibility', 'eligibility_category'.
  //
  // Most yes/no questions in this section quote USCIS form text
  // verbatim and the label IS the question. We use empty-string
  // clears for the engineer-speak helpText to keep the screen clean,
  // and reserve real copy for fields where context genuinely helps
  // (category metadata, public charge brackets, public benefits
  // received, organization memberships, long-term institutionalization).
  //
  // We deliberately do NOT add interpretive guidance to the
  // criminal / security / immigration-violation yes/no questions —
  // those should be answered by the applicant in consultation with
  // their attorney, not coached by app copy.

  // Category metadata.
  'eligibility.category.principal_applicant.first_name':
    "If you're a derivative (spouse/child of principal). Leave blank if you're the principal.",
  'eligibility.category.principal_applicant.middle_name': '',
  'eligibility.category.principal_applicant.last_name': '',
  'eligibility.category.principal_applicant.date_of_birth': '',
  'eligibility.category.principal_applicant.a_number': '',
  'eligibility.category.priority_date':
    "From your Visa Bulletin notice — usually the I-130 receipt date or I-140 priority date.",
  'eligibility.category.underlying_petition_receipt_number':
    'I-130 or I-140 receipt number — 3 letters + 10 digits.',
  'eligibility.category.other_explanation': '',
  'eligibility.category.section_245i':
    "Almost no marriage-based AOS qualifies — answer No unless you have an old (pre-April 30, 2001) petition AND are paying the $1,000 penalty fee.",

  // Public charge — brackets already have option labels via
  // optionsResolver (chunk 4 of 7d-1); the helpText here adds
  // anxiety-aware framing.
  'eligibility.public_charge.exemption_basis':
    'Most marriage-based applicants pick "Not exempt" and answer the public-charge questions that follow. Exemption categories cover VAWA self-petitioners, asylees, special-immigrant juveniles, and a few other narrow paths — pick one only if it applies to you.',
  'eligibility.public_charge.household_size':
    'Yourself + spouse + children + tax dependents.',
  'eligibility.public_charge.annual_household_income_bracket':
    'Pick the bracket — exact amounts not required.',
  'eligibility.public_charge.household_assets_bracket':
    'Savings, investments, real estate equity, vehicles.',
  'eligibility.public_charge.household_liabilities_bracket':
    'Mortgages, student loans, credit-card balances.',
  'eligibility.public_charge.highest_education_degree': '',

  // One blanket question that drives Items 63 + 64 booleans. Most
  // applicants pick "No, never" — we only ask the row details below
  // if they pick a Yes path.
  'eligibility.public_charge.benefits_history_summary':
    'Cash assistance = SSI, TANF, or state/local "General Assistance." Long-term institutional care = a hospital, nursing home, or similar facility paid by a government program. Pick "No, never" if neither has happened.',

  // Public benefits received.
  'eligibility.public_benefits_received.benefit_type':
    'SNAP / SSI / TANF / Medicaid / etc.',
  'eligibility.public_benefits_received.dollar_amount':
    'Approximate is fine.',
  'eligibility.public_benefits_received.start_date': '',
  'eligibility.public_benefits_received.end_date':
    "Leave blank if you're still receiving it.",
  'eligibility.public_benefits_received.received_means_tested':
    'Was this a Federal means-tested benefit?',

  // Long-term institutionalization.
  'eligibility.long_term_institutionalization.institution_name_and_location':
    'Hospital, treatment center, or care facility.',
  'eligibility.long_term_institutionalization.reason':
    'Medical / mental health / substance treatment / etc.',
  'eligibility.long_term_institutionalization.date_from': '',
  'eligibility.long_term_institutionalization.date_to': '',
  'eligibility.long_term_institutionalization.received_means_tested':
    'Did the institutionalization include a Federal means-tested benefit?',

  // I-864 derivative-immigrant sponsorship + Item 37 acquisition path.
  'sponsor.derivative_immigrants.is_sponsoring':
    "For most marriage-based filings the answer is No — the sponsor supports only the spouse beneficiary. Pick Yes only when there are also children being sponsored on the same packet.",
  'sponsor.sponsored_immigrants.name.legal.last': '',
  'sponsor.sponsored_immigrants.name.legal.first': '',
  'sponsor.sponsored_immigrants.name.legal.middle': '',
  'sponsor.sponsored_immigrants.relationship_to_principal':
    "E.g., Son, Daughter, Step-son.",
  'sponsor.sponsored_immigrants.date_of_birth': '',
  'sponsor.sponsored_immigrants.uscis_online_account': '',

  'sponsor.citizenship.acquired_via':
    "Pick 'Birth in the United States' if the U.S.-citizen petitioner was born in the U.S. Pick 'Naturalization' if they took the oath. Pick 'Parents' if they acquired citizenship automatically through one or both parents.",

  // Foreign address (I-130 beneficiary's residence abroad, when applicable).
  'beneficiary.foreign_address.unit_type': '',
  'beneficiary.foreign_address.unit_number': '',
  'beneficiary.foreign_address.province': '',
  'beneficiary.foreign_address.postal_code': '',

  // Organization memberships.
  'eligibility.organization_memberships.organization_name': '',
  'eligibility.organization_memberships.nature_of_group':
    'Professional, religious, political, social, etc.',
  'eligibility.organization_memberships.city': '',
  'eligibility.organization_memberships.state_or_province': '',
  'eligibility.organization_memberships.country': '',
  'eligibility.organization_memberships.date_from': '',
  'eligibility.organization_memberships.date_to': '',

  // Immigration violations / J-visa / removal — empty-string clears
  // for all yes/no questions where the label is the USCIS question
  // verbatim. Authoring interpretive guidance here without legal
  // review is risky.
  'eligibility.immigration_violations.entered_without_inspection': '',
  'eligibility.immigration_violations.lied_or_misrepresented_on_immigration_application': '',
  'eligibility.immigration_violations.falsely_claimed_us_citizenship': '',
  'eligibility.immigration_violations.smuggled_alien_into_us': '',
  'eligibility.immigration_violations.was_stowaway': '',
  'eligibility.immigration_violations.submitted_fraudulent_documentation': '',
  'eligibility.immigration_violations.failed_removal_had_reasonable_cause': '',
  'eligibility.immigration_violations.failed_removal_reasonable_cause_explanation':
    "Briefly explain. Leave blank if you answered No.",
  'eligibility.j_exchange_visitor.complied_with_two_year_rule':
    'For J-1/J-2 visa holders subject to the two-year home rule. Leave blank if you never held a J visa.',
  'eligibility.j_exchange_visitor.granted_waiver_or_recommendation': '',
  'eligibility.unlawful_presence.over_one_year_then_departed': '',
  'eligibility.unlawful_presence.reentered_after_one_year_aggregate': '',
  'eligibility.unlawful_presence.reentered_after_removal': '',
  'eligibility.removal.in_or_was_in_proceedings': '',
  'eligibility.removal.has_final_order': '',
  'eligibility.removal.has_been_excluded_or_deported': '',
  'eligibility.removal.lpr_status_rescinded': '',
  'eligibility.removal.granted_voluntary_departure_then_failed_to_depart': '',
  'eligibility.removal.had_prior_order_reinstated': '',
  'eligibility.removal.applied_for_relief_or_protection': '',
  'eligibility.prior_admission.was_denied_admission': '',
  'eligibility.prior_admission.was_denied_visa': '',
  'eligibility.prior_admission.violated_nonimmigrant_status': '',
  'eligibility.prior_admission.worked_without_authorization': '',

  // Criminal — all empty-string clears. The labels USCIS uses are
  // the question; legal counsel handles the answer.
  'eligibility.criminal.has_committed_crime': '',
  'eligibility.criminal.has_been_arrested_or_detained': '',
  'eligibility.criminal.has_pled_guilty_or_convicted': '',
  'eligibility.criminal.has_been_sentenced_or_restrained': '',
  'eligibility.criminal.has_been_defendant_in_proceeding': '',
  'eligibility.criminal.exercised_immunity_to_avoid_prosecution': '',
  'eligibility.criminal.convicted_two_offenses_combined_five_years': '',
  'eligibility.criminal.violated_controlled_substance_law': '',
  'eligibility.criminal.trafficked_controlled_substance': '',
  'eligibility.criminal.aided_controlled_substance_trafficking': '',
  'eligibility.criminal.engaged_in_prostitution': '',
  'eligibility.criminal.procured_prostitutes': '',
  'eligibility.criminal.received_proceeds_from_prostitution': '',
  'eligibility.criminal.induced_commercial_sex_acts_by_force': '',
  'eligibility.criminal.engaged_in_money_laundering': '',
  'eligibility.criminal.intends_illegal_gambling_or_commercialized_vice': '',
  'eligibility.criminal.trafficked_persons_for_servitude': '',
  'eligibility.criminal.aided_human_trafficking': '',
  'eligibility.criminal.benefited_from_family_member_human_trafficking': '',
  'eligibility.criminal.benefited_from_family_member_trafficking': '',
  'eligibility.criminal.violated_religious_freedoms_as_official': '',

  // Security — same approach. Empty-string clears across the board.
  'eligibility.security.intends_endanger_us_safety': '',
  'eligibility.security.intends_espionage_or_sabotage': '',
  'eligibility.security.intends_export_violation': '',
  'eligibility.security.intends_other_unlawful_activity': '',
  'eligibility.security.intends_to_overthrow_government': '',
  'eligibility.security.committed_terrorist_activity': '',
  'eligibility.security.member_of_terrorist_group': '',
  'eligibility.security.recruited_for_terrorist_group': '',
  'eligibility.security.provided_material_support_to_terrorist_group': '',
  'eligibility.security.provided_material_support_to_terrorist_group_alt': '',
  'eligibility.security.received_weapons_training': '',
  'eligibility.security.transported_weapons': '',
  'eligibility.security.committed_torture_or_genocide': '',
  'eligibility.security.killed_any_person': '',
  'eligibility.security.severely_injured_any_person': '',
  'eligibility.security.engaged_in_nonconsensual_sexual_contact': '',
  'eligibility.security.served_in_irregular_armed_unit': '',
  'eligibility.security.served_in_detention_facility': '',
  'eligibility.security.recruited_child_soldier': '',
  'eligibility.security.used_child_in_hostilities': '',
  'eligibility.security.member_of_armed_group_with_weapons': '',
  'eligibility.security.member_of_communist_or_totalitarian_party': '',
  'eligibility.security.limited_religious_belief': '',
  'eligibility.security.adverse_foreign_policy_consequences': '',
  'eligibility.security.associated_with_violent_individual_member': '',
  'eligibility.security.associated_with_violent_individual_committed': '',
  'eligibility.security.associated_with_violent_individual_received_training': '',
  'eligibility.security.associated_with_violent_individual_recruited': '',
  'eligibility.security.associated_with_violent_individual_supported_activity': '',
  'eligibility.security.associated_with_violent_individual_supported_actor': '',

  // Miscellaneous — same.
  'eligibility.miscellaneous.intends_polygamy': '',
  'eligibility.miscellaneous.assisted_in_child_abduction': '',
  'eligibility.miscellaneous.voted_unlawfully': '',
  'eligibility.miscellaneous.renounced_us_citizenship_to_avoid_taxation': '',
  'eligibility.miscellaneous.applied_for_armed_forces_exemption_as_foreign_national': '',
  'eligibility.miscellaneous.relieved_or_discharged_armed_forces_as_foreign_national': '',
  'eligibility.miscellaneous.convicted_of_armed_forces_desertion': '',
  'eligibility.miscellaneous.accompanying_helpless_inadmissible_person': '',
  'eligibility.miscellaneous.nationality_or_status_when_left_us':
    "Status when you last departed (B-2, F-1, EWI, etc.). Leave blank if N/A.",

  // === Sponsor — identity, addresses, marriage, parents (Phase 7d-2 chunk 7) ===
  // Sections: 'sponsor_identity', 'sponsor_physical_address',
  // 'sponsor_marriage', 'sponsor_parents'.
  // Many fields mirror the beneficiary side — copy here is more
  // sparing because the sponsor info section's intro already names
  // the role. Empty-string clears for sub-fields whose labels speak
  // for themselves.

  // Sponsor name.
  'sponsor.name.legal.first':
    "As on their passport, driver's license, or government ID.",
  'sponsor.name.legal.middle': "Leave blank if none.",
  'sponsor.name.legal.last': '',
  'sponsor.name.other.first': '',
  'sponsor.name.other.middle': '',
  'sponsor.name.other.last': '',

  // Sponsor bio.
  'sponsor.birth.date': '',
  'sponsor.birth.place.city': '',
  'sponsor.birth.place.country': '',
  'sponsor.demographics.sex': '',
  'sponsor.citizenship.status':
    "U.S. citizen, U.S. national, or LPR.",

  // Sponsor identifiers.
  'sponsor.identifiers.ssn': '9-digit U.S. SSN.',
  'sponsor.identifiers.alien_registration_number':
    "U.S.-citizens by birth typically don't have one.",
  'sponsor.identifiers.uscis_online_account_number':
    "Leave blank if they don't have a myUSCIS account.",

  // Sponsor current physical address.
  'sponsor.current_physical_address.street_number_and_name': '',
  'sponsor.current_physical_address.unit_number': '',
  'sponsor.current_physical_address.city': '',
  'sponsor.current_physical_address.state': '',
  'sponsor.current_physical_address.zip_code': '',
  'sponsor.current_physical_address.province': '',
  'sponsor.current_physical_address.postal_code': '',
  'sponsor.current_physical_address.residence.date_from':
    "Approximate month is fine.",

  // Sponsor mailing address.
  'sponsor.mailing_address.in_care_of_name':
    "Name on the mailbox if not the sponsor.",
  'sponsor.mailing_address.street_number_and_name': '',
  'sponsor.mailing_address.unit_number': '',
  'sponsor.mailing_address.city': '',
  'sponsor.mailing_address.state': '',
  'sponsor.mailing_address.zip_code': '',
  'sponsor.mailing_address.province': '',
  'sponsor.mailing_address.postal_code': '',

  // Sponsor address history.
  'sponsor.address_history.street_number_and_name': '',
  'sponsor.address_history.unit_number': '',
  'sponsor.address_history.city': '',
  'sponsor.address_history.state': '',
  'sponsor.address_history.zip_code': '',
  'sponsor.address_history.province': '',
  'sponsor.address_history.postal_code': '',
  'sponsor.address_history.date_from': '',
  'sponsor.address_history.date_to':
    "Leave blank if they still live there.",

  // Sponsor domicile.
  'sponsor.domicile.country':
    "Their permanent home — for most U.S. citizens, the United States.",

  // Sponsor marriage.
  'sponsor.marital.times_married':
    'Include the current marriage to you.',
  'sponsor.current_spouse.name.legal.first':
    "Usually you — first name of the sponsor's current spouse.",
  'sponsor.current_spouse.name.legal.middle': '',
  'sponsor.current_spouse.name.legal.last': '',
  'sponsor.current_spouse.marriage.date': '',
  'sponsor.current_spouse.marriage.place.city': '',
  'sponsor.current_spouse.marriage.place.region': '',
  'sponsor.current_spouse.marriage.place.country': '',
  'sponsor.prior_marriages.spouse.name.legal.first': '',
  'sponsor.prior_marriages.spouse.name.legal.middle': '',
  'sponsor.prior_marriages.spouse.name.legal.last': '',
  'sponsor.prior_marriages.date_ended':
    'Divorce, death, or annulment date.',

  // Sponsor parents (mirrors beneficiary parents).
  'sponsor.parent_one.name.legal.first': '',
  'sponsor.parent_one.name.legal.middle': '',
  'sponsor.parent_one.name.legal.last': '',
  'sponsor.parent_one.birth.date': '',
  'sponsor.parent_one.demographics.sex': '',
  'sponsor.parent_one.birth.place.country': '',
  'sponsor.parent_one.current_residence.city':
    "If deceased, where they lived at time of death.",
  'sponsor.parent_one.current_residence.country': '',

  'sponsor.parent_two.name.legal.first': '',
  'sponsor.parent_two.name.legal.middle': '',
  'sponsor.parent_two.name.legal.last': '',
  'sponsor.parent_two.birth.date': '',
  'sponsor.parent_two.demographics.sex': '',
  'sponsor.parent_two.birth.place.country': '',
  'sponsor.parent_two.current_residence.city':
    "If deceased, where they lived at time of death.",
  'sponsor.parent_two.current_residence.country': '',

  // === Sponsor — household, financials, basis (Phase 7d-2 chunk 8) ===
  // Sections: 'sponsor_household', 'financials', 'sponsor_basis_for_filing'.
  // I-864 Affidavit of Support is the most numerically dense part of
  // the form. Copy here helps applicants pick the right line items
  // and understand what counts as income vs assets.

  // Household — I-864 Part 5 household size (Lines 2–7).
  'sponsor.household.yourself_count':
    'Always 1 — sponsor counts themselves. Pre-filled.',
  'sponsor.household.spouse_as_immigrant_count':
    'Usually 1 (for marriage-based AOS). 0 only if you sponsor a non-spouse relative.',
  'sponsor.household.dependent_children_count':
    "Sponsor's dependent children in the household.",
  'sponsor.household.other_dependents_count':
    'Other dependents not counted above (e.g., elderly parents). 0 if none.',
  'sponsor.household.income_contributors_count':
    'Auto-computed from Part 6 contributors below — no need to fill manually.',
  'sponsor.household.total_size':
    'Auto-computed at download. USCIS uses this for the FPG threshold.',

  // Household-members income (repeatable).
  'sponsor.household_members_income.name':
    "Household member whose income you're counting (e.g., adult child living at home).",
  'sponsor.household_members_income.relationship':
    'They must live in the household and agree to be counted.',
  'sponsor.household_members_income.current_income': '',

  // Sponsor income.
  'sponsor.income.individual_annual':
    "Before taxes. From Form 1040 Total Income line.",
  'sponsor.income.household_total_annual':
    'Compared against 125% × federal poverty guideline.',
  'sponsor.income.intending_immigrant_name':
    "Only if counting the immigrant's income that continues post-LPR. Most applicants leave blank.",
  'sponsor.income.not_required_to_file_tax_return':
    "Yes only if income was below IRS threshold. USCIS still wants evidence.",

  // Sponsor tax history (repeatable, 3 most-recent years).
  'sponsor.tax_history.tax_year':
    'USCIS asks for the most recent 3 years.',
  'sponsor.tax_history.total_income':
    'Line 9 of Form 1040 (or equivalent on older versions).',

  // Sponsor assets (I-864 Part 7).
  'sponsor.assets.cash_and_accounts_balance': '',
  'sponsor.assets.stocks_bonds_value':
    "At current market value.",
  'sponsor.assets.real_estate_net_value':
    "Market value minus mortgages. Exclude the primary home unless additional properties.",
  'sponsor.assets.total': '',
  'sponsor.assets.household_members_total':
    'Same categories as the sponsor.',
  'sponsor.assets.intending_immigrant_cash_and_accounts_balance':
    "Only if you're counting your own assets.",
  'sponsor.assets.intending_immigrant_stocks_bonds_value': '',
  'sponsor.assets.intending_immigrant_real_estate_net_value': '',
  'sponsor.assets.intending_immigrant_total': '',
  'sponsor.assets.grand_total':
    'USCIS uses 5× (3× for spouses of USCs) when income alone is insufficient.',

  // Basis for filing.
  'sponsor.basis.for_filing':
    "Spouse-of-USC filings usually pick 'Petitioner (I-130 / I-129F filer).'",
  'sponsor.basis.ownership_relationship':
    "Only for 5%-owner of an I-140 petitioner business.",
  'sponsor.basis.ownership_entity_name':
    "Name of the petitioning entity.",
  'sponsor.basis.substitute_relationship':
    "Substitute sponsor's relationship to the immigrant.",
  'sponsor.petition.immigrant_is_in_us':
    "Always Yes for AOS. Pre-checked.",
  'sponsor.petition.total_immigrants_count':
    "Usually 1 (marriage-based AOS, one immigrant). Pre-filled.",

  // === Documents (Phase 7d-2 chunk 9) ===
  // Section: 'documents' — file uploads grouped by what they prove.
  //
  // Copy on document-upload fields tells the user WHAT to upload and
  // WHY USCIS asks for it. Document quality matters (color, legible,
  // both sides where applicable) — that guidance lives here too.

  // Beneficiary identity.
  'document.beneficiary_identity.birth_certificate':
    "Color scan, both sides if content. If not in English, also upload a certified translation below.",
  'document.beneficiary_identity.birth_certificate_translation':
    'Required if the original is not in English.',
  'document.beneficiary_identity.passport_bio_page':
    'Bio-data page (photo + personal info). Both pages if your passport has two.',
  'document.beneficiary_identity.passport_visa_pages':
    'Pages with any U.S. visas, stamps, or stickers. Skip blank pages.',
  'document.beneficiary_identity.passport_photos':
    '2×2 inch, white background, taken in the last 30 days. CVS / Walgreens / post offices can take these.',
  'document.beneficiary_identity.government_photo_id':
    "Driver's license, state ID, or passport bio page. Both sides.",

  // Beneficiary status.
  'document.beneficiary_status.current_ead_or_ap':
    "Front and back of your current EAD or advance parole. Skip if you don't have one.",

  // Marriage / bona fide marriage evidence.
  'document.marriage.certificate':
    'Color scan, all pages.',
  'document.marriage.beneficiary_prior_marriage_termination':
    "Divorce decree, death certificate, or annulment order from a prior marriage.",
  'document.marriage.sponsor_prior_marriage_termination':
    "Same — for any prior marriage of the sponsor.",
  'document.marriage.bona_fide_joint_finances':
    'Joint bank statements, lease/mortgage, utility bills, tax returns. Show ongoing co-mingling, not one snapshot.',
  'document.marriage.bona_fide_photos_together':
    'Over time — wedding, family events, vacations. Variety beats quantity.',
  'document.marriage.bona_fide_third_party_affidavits':
    'Friends / family / neighbors describing how they know you as a couple. Specific shared moments help.',

  // Children documents.
  'document.children.birth_certificate':
    "With certified English translation if needed.",
  'document.children.passport_bio_page':
    "If they have one.",

  // Sponsor identity.
  'document.sponsor_identity.us_citizen_evidence_passport':
    "Bio page of the sponsor's U.S. passport.",
  'document.sponsor_identity.us_citizen_evidence_birth_certificate':
    "Sponsor's U.S. birth certificate.",
  'document.sponsor_identity.us_citizen_evidence_naturalization':
    "Sponsor's Form N-550 or N-570.",
  'document.sponsor_identity.us_citizen_evidence_citizenship_certificate':
    "Form N-560 or N-561 (citizen at birth abroad).",
  'document.sponsor_identity.lpr_green_card':
    'Both sides of the green card.',
  'document.sponsor_identity.government_photo_id':
    "Skip if their U.S. passport (above) is sufficient.",

  // Sponsor financial.
  'document.sponsor_financial.tax_return_most_recent':
    "Full Form 1040 with schedules + W-2s/1099s. IRS transcripts (irs.gov/transcripts) often preferred.",
  'document.sponsor_financial.tax_return_prior_years':
    'Optional — only when current income is borderline.',
  'document.sponsor_financial.recent_pay_stubs':
    "Last 6 months, showing year-to-date.",
  'document.sponsor_financial.employer_letter':
    "On employer letterhead — title, start date, current salary.",
  'document.sponsor_financial.self_employment_evidence':
    'Business license, Schedule C, or business bank statements.',

  // Screening / records.
  'document.screening.criminal_record_disposition':
    "Court disposition for each arrest, even if dropped or expunged. Leave blank if no arrests.",
  'document.screening.conviction_disposition':
    'Court records — sentence, probation terms, completion.',
  'document.screening.military_records':
    'DD-214 for U.S. military, equivalent for foreign.',
  'document.screening.selective_service_registration':
    'For males 18-26. Registration confirmation from sss.gov.',

  // === Biographic — applicant + sponsor (Phase 7d-2 chunk 10) ===
  // Section: 'biographic' — height, weight, eye color, hair color,
  // race, ethnicity. USCIS Form I-485 Part 2 collects these for
  // background-check purposes.

  'beneficiary.biographic.ethnicity':
    'Hispanic / Latino, or not. USCIS asks separately from race.',
  'beneficiary.biographic.race':
    'Multiple selections allowed.',
  'beneficiary.biographic.height_feet': '',
  'beneficiary.biographic.height_inches': '',
  'beneficiary.biographic.weight_pounds':
    'Approximate is fine.',
  'beneficiary.biographic.eye_color': '',
  'beneficiary.biographic.hair_color':
    'Use "Bald" if applicable.',

  // Sponsor biographic — same fields, less commentary needed.
  'sponsor.biographic.ethnicity': '',
  'sponsor.biographic.race': '',
  'sponsor.biographic.height_feet': '',
  'sponsor.biographic.height_inches': '',
  'sponsor.biographic.weight_pounds': '',
  'sponsor.biographic.eye_color': '',
  'sponsor.biographic.hair_color': '',

  // Sponsor contact (in financials section).
  'sponsor.contact.daytime_phone':
    "A number USCIS can reach them at during business hours.",
  'sponsor.contact.mobile_phone':
    "If different from daytime.",
  'sponsor.contact.email': '',

  // === Petition type (Phase 7d-2 chunk 10 — continued) ===
  // Section: 'petition_type'.

  'application.petition_type.child_subtype':
    "Subcategory: legitimate child, stepchild, adopted child.",
  'application.petition_type.petitioner_status_through_adoption':
    'Was the petitioner formally adopted?',
  'application.petition_type.sibling_by_adoption':
    'Siblings through adoption?',

  // === Accommodation requests ===
  'application.accommodation.deaf_sign_language':
    'Which sign language (ASL, BSL, etc.)?',
  'application.accommodation.blind_accommodation_description':
    'E.g., Braille or large-print interview materials.',
  'application.accommodation.other_disability_description':
    'Mobility, cognitive, language-comprehension, etc.',

  // === Additional information (Form Part 14 / overflow) ===
  'application.additional_info.part_number':
    'E.g., Part 1, Part 5.',
  'application.additional_info.item_number': '',
  'application.additional_info.page_number': '',
  'application.additional_info.content':
    'Used when the main answer space ran out.',

  // === Signatures (Print & mail step) ===
  // Final signature attestation — verify you read / understand
  // before printing and mailing.

  'beneficiary.signature.statement_read_english':
    'Yes if you read the form in English yourself.',
  'beneficiary.signature.statement_read_via_interpreter':
    'Yes if an interpreter read it to you.',
  'beneficiary.signature.preferred_language':
    'Only if "Read via interpreter" is Yes.',
  'beneficiary.signature.signed':
    "Must be a physical pen signature — USCIS doesn't accept electronic signatures on I-485.",
  'beneficiary.signature.date':
    'Date you signed.',

  // Sponsor signature.
  'sponsor.signature.statement_read_english':
    "Yes if the sponsor read the form in English.",
  'sponsor.signature.statement_read_via_interpreter':
    "Yes if an interpreter read it to them.",
  'sponsor.signature.preferred_language': '',
  'sponsor.signature.contract_acknowledged':
    'The sponsor is legally bound by the I-864 contract terms.',
  'sponsor.signature.signed':
    'Required before mailing.',
  'sponsor.signature.date': '',
};

/**
 * Returns user-facing help text for a field, or `undefined` when
 * the field hasn't been authored yet (caller falls back to
 * `field.helpText` from the catalog).
 */
export function getUserHelpText(fieldId: FieldId): string | undefined {
  return USER_HELP_TEXT[fieldId as string];
}

/**
 * Substitutes the catalog's `helpText` with a user-facing string from
 * the userHelpText overlay when one is authored for this field.
 * Returns the original field unchanged when no override exists, so
 * legacy engineer-facing helpText keeps rendering until curated.
 *
 * Used at every primitive boundary (FieldRenderer + composite mount
 * sites in SectionRenderer) so users never see widget IDs or legacy
 * property names regardless of which path the field takes through
 * the dispatcher.
 */
/**
 * Memoization cache for `withUserHelpText`. The function is a pure
 * function of the field reference (catalog fields are referentially
 * stable), so we can return the same object for repeated calls. This
 * matters because `withUserHelpText` is invoked on every render of
 * every primitive that consumes a field — without the cache, every
 * render allocates a fresh object via `{ ...field, helpText: ... }`,
 * which triggers `React.memo` misses on `SelectInput` /
 * `MultiSelectInput` and re-spreads ~580 catalog fields per fast type.
 */
let helpTextCache = new WeakMap<FieldDefinition, FieldDefinition>();

/**
 * Test seam — clears the memoization cache.
 *
 * `WeakMap` has no `clear()` method, so we reassign the binding to a
 * fresh map. Tests that mutate field references between assertions
 * (or that reuse synthetic field objects across cases) rely on this
 * to avoid leaking projections between tests.
 */
export function __resetHelpTextCacheForTests(): void {
  helpTextCache = new WeakMap();
}

/**
 * Engineer-facing helpText detector.
 *
 * Many catalog fields carry helpText that's authored for the
 * pdf-engine maintainers, not the applicant ("I-485 Pt8Line1_YesNo…",
 * "ISO 3166-1 alpha-2 code. Drives state/ZIP vs province/postal
 * visibility within the repeatable group instance."). When a field
 * has NO entry in `USER_HELP_TEXT` AND its catalog helpText looks
 * engineer-facing, we strip it so the applicant doesn't see widget
 * IDs, ISO-standard codes, or engine-internal jargon.
 *
 * Markers we treat as engineer-facing (case-insensitive):
 *   - `Pt<digits>Line<digits>` — USCIS widget IDs (Pt8Line1, Pt2Line5)
 *   - `Part \d` — USCIS form-part references
 *   - `_YesNo` / `_Tx` / `_DateField` — widget type suffixes
 *   - `CB_` — PDF checkbox widget prefix
 *   - `form1[` — PDF widget addressing syntax
 *   - `subform` — PDF subform reference
 *   - `widget` — any "widget" / "widgets" / "widgetValueProjection"
 *   - `PDF` — engineering reference to the underlying form artifact
 *   - `Legacy` — references to legacy applicant-web shape
 *   - `pdf-engine` / `applicant UI` — internal package names
 *   - `Phase \d` / `Slice \d` — internal phase/slice references
 *   - `defineFieldId` — engine internals
 *   - `ISO \d` / `alpha-\d` — encoding/standard references (ISO 3166)
 *   - `repeatable group` — engine collection terminology
 *   - `valueType` / `visibility rule` / `field id` — engine schema terms
 */
const ENGINEER_HELP_PATTERN =
  /Pt\d+Line\d+|Part\s+\d+|_YesNo|_Tx\b|_DateField|\bCB_|form1\[|\bsubform\b|\bwidget(s|ValueProjection)?\b|\bPDF\b|\bLegacy\s|pdf-engine|applicant UI|Phase \d|Slice \d|defineFieldId|\bISO\s+\d|\balpha-\d|\brepeatable\s+group\b|\bvalueType\b|\bvisibility\s+rule\b|\bfield\s+id\b/i;

export function isEngineerFacing(helpText: string): boolean {
  return ENGINEER_HELP_PATTERN.test(helpText);
}

/**
 * Strip USCIS form chrome from
 * displayed labels. Catalog labels often end in `(item N)` or
 * `(Item N)` to align with the printed form's item numbering. That's
 * useful for auditors but bureaucratic for applicants — the section
 * card already groups questions semantically, the item number is
 * pure form-chrome leak.
 */
export function stripLabelChrome(label: string): string {
  return label
    .replace(/\s*\(item\s+\d+\)\s*$/i, '')
    .replace(/\s*\(I-\d+\s+Pt\d+Line\d+[^)]*\)\s*$/i, '')
    // "(items 1 + 2 + 3)" — derived-total arithmetic from the I-864
    // catalog. The line numbers don't exist anywhere on screen, so the
    // instruction is meaningless to applicants.
    .replace(/\s*\(items?\s+\d+(?:\s*[+,]\s*\d+)+\)\s*$/i, '')
    // "(visible item 14 override)" — internal USCIS form-routing
    // bookkeeping. Same family as the bare "(item N)" pattern but with
    // a trailing qualifier word ("override" / "name fill-in" / etc.).
    .replace(/\s*\((?:visible\s+)?item\s+\d+(?:\s+[A-Za-z][\w\s-]*)?\)\s*$/i, '')
    .trim();
}

export function withUserHelpText(field: FieldDefinition): FieldDefinition {
  const cached = helpTextCache.get(field);
  if (cached !== undefined) return cached;
  const userHelp = getUserHelpText(field.id);
  // Also strip USCIS form-chrome from displayed
  // labels (`(item N)` etc). And when no userHelpText override exists
  // but the catalog helpText looks engineer-facing, clear it so the
  // applicant doesn't see widget IDs.
  const cleanLabel = stripLabelChrome(field.label);
  const labelChanged = cleanLabel !== field.label;

  if (userHelp === undefined) {
    // No override. If the catalog helpText is engineer-facing, strip
    // it; otherwise keep it (some catalog helpers ARE applicant-
    // friendly).
    const catalogHelp = field.helpText;
    const shouldStripCatalogHelp =
      typeof catalogHelp === 'string' && isEngineerFacing(catalogHelp);

    if (!labelChanged && !shouldStripCatalogHelp) {
      helpTextCache.set(field, field);
      return field;
    }
    const projected: FieldDefinition = {
      ...field,
      ...(labelChanged ? { label: cleanLabel } : {}),
      ...(shouldStripCatalogHelp ? { helpText: '' } : {}),
    };
    helpTextCache.set(field, projected);
    return projected;
  }
  // Override exists. Apply it + clean label.
  if (field.helpText === userHelp && !labelChanged) {
    helpTextCache.set(field, field);
    return field;
  }
  const projected: FieldDefinition = {
    ...field,
    label: cleanLabel,
    helpText: userHelp,
  };
  helpTextCache.set(field, projected);
  return projected;
}
